import { X, Monitor, Smartphone } from 'lucide-react';
import { Button } from '../../ui/button';
import { cn } from '../../../lib/utils';
import { useState } from 'react';

interface PostPreviewProps {
  title: string;
  coverImageUrl: string;
  onClose: () => void;
}

type PreviewDevice = 'desktop' | 'mobile';

export function PostPreview({ title, coverImageUrl, onClose }: PostPreviewProps) {
  const [device, setDevice] = useState<PreviewDevice>('desktop');

  return (
    <div className="fixed inset-0 z-50 bg-background flex flex-col overflow-hidden">
      {/* Top bar */}
      <div className="flex items-center justify-between px-6 py-3 border-b bg-background shrink-0">
        <div className="flex items-center gap-4">
          <h2 className="font-semibold text-sm">Visualização</h2>

          {/* Device toggle */}
          <div className="flex items-center rounded-md border p-0.5 gap-0.5">
            <button
              type="button"
              title="Desktop"
              onClick={() => setDevice('desktop')}
              className={cn(
                'p-1.5 rounded transition-colors',
                device === 'desktop'
                  ? 'bg-primary text-primary-foreground'
                  : 'text-muted-foreground hover:text-foreground'
              )}
            >
              <Monitor className="h-4 w-4" />
            </button>
            <button
              type="button"
              title="Mobile"
              onClick={() => setDevice('mobile')}
              className={cn(
                'p-1.5 rounded transition-colors',
                device === 'mobile'
                  ? 'bg-primary text-primary-foreground'
                  : 'text-muted-foreground hover:text-foreground'
              )}
            >
              <Smartphone className="h-4 w-4" />
            </button>
          </div>
        </div>

        <Button variant="outline" size="sm" onClick={onClose}>
          <X className="h-4 w-4 mr-2" />
          Fechar
        </Button>
      </div>

      {/* Preview area */}
      <div className="flex-1 overflow-y-auto bg-muted/30 p-6 flex justify-center">
        <div
          className={cn(
            'bg-white dark:bg-zinc-950 rounded-lg shadow-lg overflow-hidden transition-all duration-300',
            device === 'desktop'
              ? 'w-full max-w-4xl'
              : 'w-[390px] max-w-full'
          )}
        >
          {/* Article content */}
          <article className="min-h-[600px]">
            {/* Cover image */}
            {coverImageUrl && (
              <div className="aspect-video overflow-hidden bg-muted">
                <img
                  src={coverImageUrl}
                  alt={title || 'Capa do post'}
                  className="w-full h-full object-cover"
                />
              </div>
            )}

            {/* Content area */}
            <div className={cn('px-8 py-10', device === 'mobile' && 'px-5 py-6')}>
              {/* Post header */}
              <header className="mb-8">
                <h1
                  className={cn(
                    'font-bold leading-tight text-zinc-900 dark:text-zinc-50',
                    device === 'desktop' ? 'text-4xl' : 'text-2xl'
                  )}
                >
                  {title || 'Título do post'}
                </h1>
              </header>

              {/* Content placeholder */}
              <div className="prose prose-zinc dark:prose-invert max-w-none">
                <div className="space-y-4">
                  <div className="rounded-lg border border-dashed border-muted-foreground/30 p-6 text-center">
                    <p className="text-sm text-muted-foreground">
                      O conteúdo do editor será renderizado aqui no modo de leitura.
                    </p>
                    <p className="text-xs text-muted-foreground/60 mt-1">
                      Salve o post para visualizar o conteúdo completo.
                    </p>
                  </div>

                  {/* Skeleton paragraphs to simulate content */}
                  <div className="space-y-2 animate-pulse">
                    <div className="h-4 bg-muted rounded w-full" />
                    <div className="h-4 bg-muted rounded w-5/6" />
                    <div className="h-4 bg-muted rounded w-4/5" />
                  </div>
                  <div className="space-y-2 animate-pulse">
                    <div className="h-4 bg-muted rounded w-full" />
                    <div className="h-4 bg-muted rounded w-3/4" />
                    <div className="h-4 bg-muted rounded w-full" />
                    <div className="h-4 bg-muted rounded w-2/3" />
                  </div>
                  <div className="h-40 bg-muted rounded-lg animate-pulse" />
                  <div className="space-y-2 animate-pulse">
                    <div className="h-4 bg-muted rounded w-full" />
                    <div className="h-4 bg-muted rounded w-5/6" />
                    <div className="h-4 bg-muted rounded w-full" />
                  </div>
                </div>
              </div>
            </div>
          </article>
        </div>
      </div>
    </div>
  );
}
